import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { EyeIcon, LeafIcon, AtomIcon, SunIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
export function SolutionSection() {
  const {
    t
  } = useLanguage();
  return <section id="libro" className="relative w-full py-8 sm:py-14" data-id="element-350">
      <div className="max-w-6xl mx-auto px-6" data-id="element-351">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-60px'
      }} transition={{
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }} className="text-center mb-6 sm:mb-10" data-id="element-352">
          <p className="font-sans text-sm text-gold tracking-[0.2em] uppercase mb-4" data-id="element-353">
            {t('solution.eyebrow')}
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-white" data-id="element-354">
            {t('solution.title')}
          </h2>
        </motion.div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-60px'
      }} className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-355">
          {/* Card 1 — Pineal Gland */}
          <motion.div variants={cardVariants} className="group relative" data-id="element-356">
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-gold/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-xl" data-id="element-357" />
            <div className="relative rounded-xl bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] p-6 sm:p-8 h-full overflow-hidden" data-id="element-358">
              <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-gold/40 via-gold/10 to-transparent" data-id="element-359" />
              <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mb-6" data-id="element-360">
                <EyeIcon className="w-6 h-6 text-gold" data-id="element-361" />
              </div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-white mb-3" data-id="element-362">
                {t('solution.pineal.title')}
              </h3>
              <p className="font-sans text-white/40 leading-relaxed" data-id="element-363">
                {t('solution.pineal.desc')}
              </p>
            </div>
          </motion.div>

          {/* Card 2 — Plant Medicine */}
          <motion.div variants={cardVariants} className="group relative" data-id="element-364">
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-purple-electric/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-xl" data-id="element-365" />
            <div className="relative rounded-xl bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] p-6 sm:p-8 h-full overflow-hidden" data-id="element-366">
              <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-purple-electric/40 via-purple-electric/10 to-transparent" data-id="element-367" />
              <div className="w-12 h-12 rounded-lg bg-purple-electric/10 flex items-center justify-center mb-6" data-id="element-368">
                <LeafIcon className="w-6 h-6 text-purple-electric" data-id="element-369" />
              </div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-white mb-3" data-id="element-370">
                {t('solution.plant.title')}
              </h3>
              <p className="font-sans text-white/40 leading-relaxed" data-id="element-371">
                {t('solution.plant.desc')}
              </p>
            </div>
          </motion.div>

          {/* Card 3 — Quantum Physics */}
          <motion.div variants={cardVariants} className="group relative" data-id="element-372">
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-astral/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-xl" data-id="element-373" />
            <div className="relative rounded-xl bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] p-6 sm:p-8 h-full overflow-hidden" data-id="element-374">
              <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-astral/40 via-astral/10 to-transparent" data-id="element-375" />
              <div className="w-12 h-12 rounded-lg bg-astral/10 flex items-center justify-center mb-6" data-id="element-376">
                <AtomIcon className="w-6 h-6 text-astral" data-id="element-377" />
              </div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-white mb-3" data-id="element-378">
                {t('solution.quantum.title')}
              </h3>
              <p className="font-sans text-white/40 leading-relaxed" data-id="element-379">
                {t('solution.quantum.desc')}
              </p>
            </div>
          </motion.div>

          {/* Card 4 — Integration */}
          <motion.div variants={cardVariants} className="group relative" data-id="element-380">
            <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-gold/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 blur-xl" data-id="element-381" />
            <div className="relative rounded-xl bg-white/[0.03] backdrop-blur-xl border border-white/[0.06] p-6 sm:p-8 h-full overflow-hidden" data-id="element-382">
              <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-gold/40 via-gold/10 to-transparent" data-id="element-383" />
              <div className="w-12 h-12 rounded-lg bg-gold/10 flex items-center justify-center mb-6" data-id="element-384">
                <SunIcon className="w-6 h-6 text-gold" data-id="element-385" />
              </div>
              <h3 className="font-serif text-xl sm:text-2xl font-bold text-white mb-3" data-id="element-386">
                {t('solution.integration.title')}
              </h3>
              <p className="font-sans text-white/40 leading-relaxed" data-id="element-387">
                {t('solution.integration.desc')}
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}